import { useState, useEffect, useCallback } from 'react';

const FAVORITES_EVENT = 'instashop-favorites-changed';

/**
 * Αγαπημένα ανά eshop: το κλειδί localStorage είναι το merchant owner_id.
 * Χωρίς merchantId δεν γράφουμε πουθενά (αποφυγή κοινής λίστας "default" μεταξύ καταστημάτων).
 */
export function useFavorites(merchantId) {
    const storageKey = merchantId ? `instashop_favorites_${merchantId}` : null;

    const readIds = useCallback(() => {
        if (!storageKey) return [];
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
        } catch {
            return [];
        }
    }, [storageKey]);

    const [favoriteIds, setFavoriteIds] = useState(readIds);

    useEffect(() => {
        setFavoriteIds(readIds());
    }, [readIds]);

    useEffect(() => {
        const onStorage = (e) => {
            if (storageKey && e.key === storageKey) setFavoriteIds(readIds());
        };
        const onCustom = () => setFavoriteIds(readIds());
        window.addEventListener('storage', onStorage);
        window.addEventListener(FAVORITES_EVENT, onCustom);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener(FAVORITES_EVENT, onCustom);
        };
    }, [storageKey, readIds]);

    const persist = useCallback(
        (nextIds) => {
            if (!storageKey) return;
            localStorage.setItem(storageKey, JSON.stringify(nextIds));
            setFavoriteIds(nextIds);
            window.dispatchEvent(new Event(FAVORITES_EVENT));
        },
        [storageKey]
    );

    const toggleFavorite = useCallback(
        (productId) => {
            if (!storageKey || !productId) return;
            const cur = readIds();
            if (cur.includes(productId)) {
                persist(cur.filter((id) => id !== productId));
            } else {
                persist([...cur, productId]);
            }
        },
        [persist, readIds, storageKey]
    );

    const removeFavorite = useCallback(
        (productId) => {
            if (!storageKey || !productId) return;
            persist(readIds().filter((id) => id !== productId));
        },
        [persist, readIds, storageKey]
    );

    const isFavorite = useCallback((productId) => favoriteIds.includes(productId), [favoriteIds]);

    return { favoriteIds, toggleFavorite, removeFavorite, isFavorite };
}
